/**
 * menu-config-repo-firestore.ts — impl Firestore do repositório (Parte 4)
 *
 * A ÚNICA peça deste feature que lê/escreve no Firestore. Implementa o contrato
 * `MenuConfigRepo` (Parte 3) sobre o DOC ÚNICO por tenant em
 * `tenants/{tenantId}/config/menu`, com o `toDoc`/`fromDoc` puros (Parte 2). A
 * impl memória (`criarRepoMemoria`) continua sendo a de demo/sessão; esta entra
 * no modo real via wiring do provider (§3.4 do desenho).
 *
 * Semântica fail-safe (§3.3 do desenho):
 *   - `ler`: `getDoc`; doc `!exists` => `MENU_CONFIG_DEFAULT` (tudo ligado). A
 *     ausência é estado válido (tenant recém-provisionado), nunca erro.
 *   - `observar`: `onSnapshot`; doc `!exists` => emite o default. A sidebar segue
 *     mostrando tudo que o RBAC permite; a config só RESTRINGE.
 *   - `salvar(c)`: `setDoc` merge=false (o doc é o estado COMPLETO dos toggles,
 *     idempotente). `atualizadoEm` (serverTimestamp) é injetado AQUI, fora da
 *     camada pura `toDoc`.
 *
 * Lineage e anti-planting: `toDoc(c, ctx)` carimba `tenantId`/`atualizadoPorUid`/
 * `atualizadoPor` a partir do `deps` recebido no wiring (nunca do form). As rules
 * (Parte 5) exigem `tenantId == tenantId` do path e `atualizadoPorUid ==
 * auth.uid`, batendo com o que carimbamos aqui. `atualizadoPor` é nome de
 * exibição, NUNCA email/CPF (zero PII).
 */

import {
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type Firestore,
} from "firebase/firestore";
import type { MenuConfig } from "./menu-config";
import type { ContextoMenuConfigDoc } from "./menu-config";
import { fromDoc, toDoc } from "./menu-config";
import type { MenuConfigRepo } from "./menu-config-repo";

// ---------------------------------------------------------------------------
// Dependências do wiring: db + lineage do tenant/usuário autenticado
// ---------------------------------------------------------------------------

/**
 * O que o wiring injeta para montar o repositório real. `tenantId`/
 * `atualizadoPorUid`/`atualizadoPor` vêm do tenant e do usuário autenticado
 * (nunca de input livre), e são carimbados no doc via `toDoc` (anti-planting).
 * `atualizadoPor` é nome de exibição, NUNCA email/CPF (zero PII).
 */
export interface DepsRepoFirestore {
  readonly db: Firestore;
  readonly tenantId: string;
  readonly atualizadoPorUid: string;
  readonly atualizadoPor: string;
}

/** Segmento raiz dos tenants. */
const COLECAO_TENANTS = "tenants";
/** Segmento da subcoleção de config e id do doc único de menu. */
const COLECAO_CONFIG = "config";
const DOC_MENU = "menu";

// ---------------------------------------------------------------------------
// criarRepoFirestore — a impl real do MenuConfigRepo
// ---------------------------------------------------------------------------

/**
 * Cria um `MenuConfigRepo` que persiste no doc único
 * `tenants/{tenantId}/config/menu`. `salvar` grava com `setDoc` (merge=false):
 * o `c` passado vira o estado completo dos toggles. Fail-safe: doc ausente na
 * leitura/observação cai no default (tudo ligado).
 */
export function criarRepoFirestore(deps: DepsRepoFirestore): MenuConfigRepo {
  const { db, tenantId, atualizadoPorUid, atualizadoPor } = deps;

  // Lineage carimbada na escrita (anti-planting: vem do wiring, não do form).
  const ctx: ContextoMenuConfigDoc = {
    tenantId,
    atualizadoPorUid,
    atualizadoPor,
  };

  const ref = () => doc(db, COLECAO_TENANTS, tenantId, COLECAO_CONFIG, DOC_MENU);

  return {
    async ler(): Promise<MenuConfig> {
      const snap = await getDoc(ref());
      // Ausência é estado válido (tenant sem config) => default (tudo ligado).
      // fromDoc(undefined) já reconstrói o default com arrays NOVOS (fail-safe).
      return snap.exists() ? fromDoc(snap.data()) : fromDoc(undefined);
    },

    async salvar(c: MenuConfig): Promise<void> {
      // Doc único: o estado completo dos toggles. merge=false (setDoc sem opção).
      // atualizadoEm (serverTimestamp) injetado aqui, fora da camada pura toDoc.
      await setDoc(ref(), {
        ...toDoc(c, ctx),
        tenantId,
        atualizadoEm: serverTimestamp(),
      });
    },

    observar(cb: (c: MenuConfig) => void): () => void {
      return onSnapshot(ref(), (snap) => {
        // Doc ausente => emite o default (não erro; a sidebar segue completa).
        cb(snap.exists() ? fromDoc(snap.data()) : fromDoc(undefined));
      });
    },
  };
}
